import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  ArrowLeft,
  Save,
  Zap,
  MapPin,
  Car,
  Clock,
  Calendar,
  AlertTriangle,
  Loader2,
  Eye,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useSurgePricing,
  useCreateSurgePricing,
  useUpdateSurgePricing,
} from "@/api-services/hooks/useSurgePricing";
import {
  useVehicleCategories,
  useCities,
  useCity,
} from "@/api-services/hooks/usePricing";
import {
  SurgePricingDTO,
  VehicleCategory,
  CityMaster,
  SurgePricingForm,
  LocalTime,
} from "@/api-services/hooks/surgePricingTypes";
import { getStoredCurrency } from "@/api-services/hooks/useSettings";

const surgePricingFormSchema = z.object({
  vehicleCategoryId: z.number().min(1, "Vehicle category is required"),
  cityId: z.number().min(1, "City is required"),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().min(1, "End time is required"),
  surgeMultiplier: z.number().min(1.0, "Surge multiplier must be at least 1.0"),
  dayOfWeek: z.string().min(1, "Day of week is required"),
  maxSurgeCap: z.number().min(0, "Max surge cap must be non-negative"),
  isActive: z.boolean(),
});

type FormData = z.infer<typeof surgePricingFormSchema>;

const AddEditSurgePricingPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  const surgeRuleId = parseInt(id || "0");

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(surgePricingFormSchema),
    defaultValues: {
      vehicleCategoryId: 0,
      cityId: 0,
      startTime: "09:00",
      endTime: "18:00",
      surgeMultiplier: 1.5,
      dayOfWeek: "MONDAY",
      maxSurgeCap: 50.0,
      isActive: true,
    },
  });

  // Fetch existing surge pricing data if editing
  const { data: pricingData, loading: loadingPricing } = useSurgePricing(
    surgeRuleId,
    {
      enabled: isEditing,
      onError: (err) => {
        toast.error(`Failed to load surge pricing: ${err.message}`);
      },
    }
  );

  // API hooks
  const createSurgePricing = useCreateSurgePricing({
    onSuccess: (data) => {
      toast.success("Surge pricing created successfully!");
      navigate("/admin/surge-pricing");
    },
    onError: (error, variables) => {
      toast.error(`Failed to create surge pricing: ${error.message}`);
    },
  });

  const updateSurgePricing = useUpdateSurgePricing({
    onSuccess: (data) => {
      toast.success("Surge pricing updated successfully!");
      navigate("/admin/surge-pricing");
    },
    onError: (error) => {
      toast.error(`Failed to update surge pricing: ${error.message}`);
    },
  });

  // Fetch dropdown options
  const { data: vehicleCategories, isLoading: loadingCategories } = useVehicleCategories({
    onError: (err) => {
      toast.error(`Failed to load vehicle categories: ${err.message}`);
    },
  });

  const { data: cities, isLoading: loadingCities } = useCities({
    onError: (err) => {
      toast.error(`Failed to load cities: ${err.message}`);
    },
  });

  // Track selected city ID to fetch complete details
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  // Fetch complete city details when a city is selected
  const { data: completeCityData, isLoading: loadingCompleteCity } = useCity(
    selectedCityId || 0,
    {
      enabled: !!selectedCityId && selectedCityId > 0,
      onError: (err) => {
        // Don't show error toast as this is optional enhancement
      },
    }
  );

  // Populate form with existing data when editing
  useEffect(() => {
    if (isEditing && pricingData && !loadingPricing) {
      const cityId = pricingData.city?.cityId || 0;
      reset({
        vehicleCategoryId: pricingData.vehicleCategoryId,
        cityId: cityId,
        startTime: formatTimeForInput(pricingData.startTime),
        endTime: formatTimeForInput(pricingData.endTime),
        surgeMultiplier: pricingData.surgeMultiplier,
        dayOfWeek: pricingData.dayOfWeek,
        maxSurgeCap: pricingData.maxSurgeCap,
        isActive: pricingData.isActive,
      });
      // Fetch complete city details for editing
      if (cityId > 0) {
        setSelectedCityId(cityId);
      }
    }
  }, [pricingData, isEditing, reset, loadingPricing]);

  const watchedValues = watch();

  const onSubmit = (data: FormData) => {
    // Validate city selection
    const selectedCity = getCityObject(data.cityId);
    if (!selectedCity) {
      toast.error("Please select a valid city");
      return;
    }

    // Validate required fields
    if (!data.vehicleCategoryId || data.vehicleCategoryId === 0) {
      toast.error("Please select a valid vehicle category");
      return;
    }

    if (!data.cityId || data.cityId === 0) {
      toast.error("Please select a valid city");
      return;
    }

    // Use complete city data if available (from single city fetch), otherwise use list data
    const cityToUse = completeCityData && (completeCityData as CityMaster).cityId === data.cityId
      ? (completeCityData as CityMaster)
      : selectedCity;

    // Prepare full city object - backend requires complete object
    // Ensure required fields are populated, use empty strings instead of null for required fields
    const cityPayload = prepareCityObject(cityToUse);

    // Prepare the payload
    const payload: SurgePricingDTO = {
      vehicleCategoryId: data.vehicleCategoryId,
      city: cityPayload,
      startTime: data.startTime + ":00", // Append seconds
      endTime: data.endTime + ":00", // Append seconds
      surgeMultiplier: data.surgeMultiplier,
      dayOfWeek: data.dayOfWeek,
      maxSurgeCap: data.maxSurgeCap,
      isActive: data.isActive,
      createdBy: isEditing ? (pricingData?.createdBy || "admin") : "admin",
      createdDate: isEditing ? pricingData?.createdDate : new Date().toISOString(),
      isDeletedValue: isEditing ? (pricingData?.isDeletedValue || false) : false,
      modifiedBy: "admin",
      modifiedDate: new Date().toISOString(),
    };

    if (isEditing && surgeRuleId) {
      // For editing, include surgeRuleId
      payload.surgeRuleId = surgeRuleId;
      updateSurgePricing.mutate({ id: surgeRuleId, data: payload });
    } else {
      // For creating, explicitly omit surgeRuleId
      // Ensure surgeRuleId is not in the payload
      const { surgeRuleId: _, ...createPayload } = payload as any;
      createSurgePricing.mutate(createPayload);
    }
  };

  const formatTimeForInput = (time: any): string => {
    if (!time) return "09:00";
    if (typeof time === 'string') {
      // If it's already a string in HH:mm format, return as is
      if (time.includes(':')) return time;
      // Otherwise, try to parse LocalTime object
      return "09:00";
    }
    // If it's a LocalTime object
    const hours = time.hour?.toString().padStart(2, '0') || '09';
    const minutes = time.minute?.toString().padStart(2, '0') || '00';
    return `${hours}:${minutes}`;
  };



  // Helper function to get city object from cities list
  const getCityObject = (cityId: number): CityMaster | null => {
    return (cities as CityMaster[])?.find(city => city.cityId === cityId) || null;
  };

  // Helper function to prepare full city object for API - ensure required fields have values
  const prepareCityObject = (city: CityMaster): CityMaster => {
    // Build complete city object with all required fields
    const cleanedCity: any = {
      cityId: city.cityId,
      code: city.code || "",
      cityName: city.cityName,
      createdBy: city.createdBy || "",
      createdDate: city.createdDate || new Date().toISOString(),
      createdSystemIp: city.createdSystemIp || "", // Always include system IP fields
      isDeletedValue: city.isDeletedValue ?? false,
      isActive: city.isActive ?? true,
      modifiedBy: city.modifiedBy || "",
      modifiedDate: city.modifiedDate || new Date().toISOString(),
      modifiedSystemIp: city.modifiedSystemIp || "", // Always include system IP fields
      status: city.status || "",
    };

    // Include optional fields only if they have values
    if (city.remarks) cleanedCity.remarks = city.remarks;

    // Handle state object - ensure required fields are present
    if (city.state && city.state.stateId) {
      cleanedCity.state = {
        stateId: city.state.stateId,
        code: city.state.code || "",
        stateName: city.state.stateName || "", // Required field - use empty string if null
        createdSystemIp: city.state.createdSystemIp || "", // Always include system IP fields
        isDeletedValue: city.state.isDeletedValue ?? false,
        isActive: city.state.isActive ?? true,
        modifiedSystemIp: city.state.modifiedSystemIp || "", // Always include system IP fields
      };

      // Include optional fields only if they have values
      if (city.state.createdBy) cleanedCity.state.createdBy = city.state.createdBy;
      if (city.state.createdDate) cleanedCity.state.createdDate = city.state.createdDate;
      if (city.state.modifiedBy) cleanedCity.state.modifiedBy = city.state.modifiedBy;
      if (city.state.modifiedDate) cleanedCity.state.modifiedDate = city.state.modifiedDate;
      if (city.state.status) cleanedCity.state.status = city.state.status;
      if (city.state.remarks) cleanedCity.state.remarks = city.state.remarks;

      // Handle country object - ensure required fields are present
      if (city.state.country && city.state.country.countryId) {
        cleanedCity.state.country = {
          countryId: city.state.country.countryId,
          code: city.state.country.code || "",
          countryName: city.state.country.countryName || "", // Required field - use empty string if null
          createdSystemIp: city.state.country.createdSystemIp || "", // Always include system IP fields
          isDeletedValue: city.state.country.isDeletedValue ?? false,
          isActive: city.state.country.isActive ?? true,
          modifiedSystemIp: city.state.country.modifiedSystemIp || "", // Always include system IP fields
        };

        // Include optional fields only if they have values
        if (city.state.country.createdBy) cleanedCity.state.country.createdBy = city.state.country.createdBy;
        if (city.state.country.createdDate) cleanedCity.state.country.createdDate = city.state.country.createdDate;
        if (city.state.country.modifiedBy) cleanedCity.state.country.modifiedBy = city.state.country.modifiedBy;
        if (city.state.country.modifiedDate) cleanedCity.state.country.modifiedDate = city.state.country.modifiedDate;
        if (city.state.country.status) cleanedCity.state.country.status = city.state.country.status;
        if (city.state.country.remarks) cleanedCity.state.country.remarks = city.state.country.remarks;
      }
    }

    return cleanedCity as CityMaster;
  };

  const formatCurrency = (value: string) => {
    if (!value) return "";
    const num = parseFloat(value);
    return isNaN(num) ? value : num.toFixed(2);
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowLeaveModal(true);
    } else {
      navigate("/admin/surge-pricing");
    }
  };

  const dayOfWeekOptions = [
    { value: "MONDAY", label: "Monday" },
    { value: "TUESDAY", label: "Tuesday" },
    { value: "WEDNESDAY", label: "Wednesday" },
    { value: "THURSDAY", label: "Thursday" },
    { value: "FRIDAY", label: "Friday" },
    { value: "SATURDAY", label: "Saturday" },
    { value: "SUNDAY", label: "Sunday" },
  ];

  if (loadingPricing && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading surge pricing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Unsaved Changes Modal */}
      <Dialog open={showLeaveModal} onOpenChange={setShowLeaveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave without saving?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveModal(false)}>
              Stay on Page
            </Button>
            <Button variant="destructive" onClick={() => navigate("/admin/surge-pricing")}>
              Leave Without Saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={handleCancel}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Surge Pricing
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {isEditing ? "Edit Surge Rule" : "Add New Surge Rule"}
          </h1>
          <p className="text-muted-foreground">
            {isEditing
              ? "Update the surge pricing rule details below."
              : "Create a new surge pricing rule to manage demand-based pricing."}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Location and Vehicle Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Location & Vehicle Configuration
            </CardTitle>
            <CardDescription>
              Select the city, vehicle category, and timing for this surge rule
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cityId">City *</Label>
                <Select
                  value={watchedValues.cityId?.toString() || ""}
                  onValueChange={(value) => {
                    const cityId = parseInt(value);
                    setValue("cityId", cityId);
                    setSelectedCityId(cityId); // Trigger fetch of complete city details
                  }}
                  disabled={loadingCities || loadingCompleteCity}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {(cities as CityMaster[])?.map((city) => (
                      <SelectItem key={city.cityId} value={city.cityId.toString()}>
                        {city.cityName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.cityId && (
                  <p className="text-sm text-destructive mt-1">{errors.cityId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="vehicleCategoryId">Vehicle Category *</Label>
                <Select
                  value={watchedValues.vehicleCategoryId?.toString() || ""}
                  onValueChange={(value) => setValue("vehicleCategoryId", parseInt(value))}
                  disabled={loadingCategories}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vehicle category" />
                  </SelectTrigger>
                  <SelectContent>
                    {(vehicleCategories as VehicleCategory[])?.map((category) => (
                      <SelectItem key={category.vehicleCategoryId} value={category.vehicleCategoryId.toString()}>
                        {category.vehicleCategoryName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.vehicleCategoryId && (
                  <p className="text-sm text-destructive mt-1">{errors.vehicleCategoryId.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dayOfWeek">Day of Week *</Label>
                <Select
                  value={watchedValues.dayOfWeek || ""}
                  onValueChange={(value) => setValue("dayOfWeek", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent>
                    {dayOfWeekOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.dayOfWeek && (
                  <p className="text-sm text-destructive mt-1">{errors.dayOfWeek.message}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={watchedValues.isActive}
                  onCheckedChange={(checked) => setValue("isActive", checked === true)}
                />
                <Label htmlFor="isActive" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed">
                  Active Rule
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timing Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Timing Configuration
            </CardTitle>
            <CardDescription>
              Set the time range and multiplier for this surge rule
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startTime">Start Time *</Label>
                <Input
                  id="startTime"
                  type="time"
                  {...register("startTime")}
                  placeholder="09:00"
                />
                {errors.startTime && (
                  <p className="text-sm text-destructive mt-1">{errors.startTime.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="endTime">End Time *</Label>
                <Input
                  id="endTime"
                  type="time"
                  {...register("endTime")}
                  placeholder="18:00"
                />
                {errors.endTime && (
                  <p className="text-sm text-destructive mt-1">{errors.endTime.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {/* <DollarSign className="w-5 h-5" /> */}
              ({getStoredCurrency()}){" "}
              Pricing Configuration
            </CardTitle>
            <CardDescription>
              Configure the surge multiplier and maximum cap
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="surgeMultiplier">Surge Multiplier *</Label>
                <Input
                  id="surgeMultiplier"
                  type="number"
                  step="0.1"
                  min="1.0"
                  max="5.0"
                  {...register("surgeMultiplier", { valueAsNumber: true })}
                  placeholder="1.5"
                />
                {errors.surgeMultiplier && (
                  <p className="text-sm text-destructive mt-1">{errors.surgeMultiplier.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="maxSurgeCap">Max Surge Cap (%)</Label>
                <Input
                  id="maxSurgeCap"
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  {...register("maxSurgeCap", { valueAsNumber: true })}
                  placeholder="50"
                />
                {errors.maxSurgeCap && (
                  <p className="text-sm text-destructive mt-1">{errors.maxSurgeCap.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createSurgePricing.loading || updateSurgePricing.loading}
          >
            {(createSurgePricing.loading || updateSurgePricing.loading) && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            <Save className="w-4 h-4 mr-2" />
            {isEditing ? "Update Surge Rule" : "Create Surge Rule"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditSurgePricingPage;